import { useApolloClient, useQuery } from '@apollo/react-hooks';
import { createStyles, Grid, makeStyles } from '@material-ui/core';
import {
	Breadcrumbs,
	BreadcrumbsText, ExtendedTheme,
	Heading,
	Link,
	Loader,
	Option,
	Radio,
	TextBlock,
	Tooltip,
	Button,
	ErrorText
} from 'neon-design-system';
import React, { useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { GET_PREMIUM_CHANNEL_BY_ID } from '../../../gql/channels';
import {
	MY_BILLING_ACCOUNTS
} from '../../../gql/users';
import { Channel, ChannelSubscriptionType } from '../../../shared/types';
import { baseUrls } from '../../../utils/baseUrls.utils';
import { getChannelSubscriptionLink } from '../../../utils/link.utils';
import { useLogger } from '../../providers/logger/Logger';
import { LogAction, LogLevel } from '../../providers/logger/logger-types';
import { FurtherAssistanceEmail } from './FurtherAssistanceEmail';
import { PremiumChannelAccountPayment } from './PremiumChannelAccountPayment';
import { PremiumChannelInvoicePayment } from './PremiumChannelInvoicePayment';
import { StripePaymentStepper } from './StripePaymentStepper';

const useStyles = makeStyles<ExtendedTheme>((theme: ExtendedTheme) =>
	createStyles({
		breadcrumbs: {
			'& nav': {
				border: 0,
				paddingBottom: 0
			}
		},
		radio: {
			borderRadius: theme.spacing(0.5),
			border: `1px solid ${theme.palette.primary.main}`,
			padding: theme.spacing(2),
			width: '100%'
		},
		disabledRadio: {
			border: `1px solid ${theme.themeColors.primary[900]}`,

			'& .MuiTypography-root': {
				color: theme.themeColors.grey[500]
			}
		},
		checkbox: {
			marginRight: theme.spacing(0.5)
		},
		errorText: {
			color: theme.palette.error.main,
			fontSize: '10px',
			marginTop: theme.spacing(0.5),
			fontWeight: 'bold'
		},
		blueGray: {
			color: theme.themeColors.blueGrey[200]
		},
		header: {
			marginBottom: theme.spacing(1)
		},
		paper: {
			padding: theme.spacing(4)
		}
	})
);

interface Account {
	id: string;
	name: string;
}

export interface SelectOption {
	label: string;
	value: string;
}

const PremiumChannelPaymentScreen = () => {
	const classes = useStyles();
	const history = useHistory();
	const { channelId } = useParams<{ channelId: string }>();
	const client = useApolloClient();
	const { printLog } = useLogger();
	const [paymentType, setPaymentType] = useState<ChannelSubscriptionType>();
	const [billingAccounts, setBillingAccounts] = useState<Option[]>([]);
	const [isPaymentFormShown, setPaymentFormShown] = useState<boolean>(false);
	const [isPaymentTypeChosen, setPaymentTypeChosen] = useState<boolean>(false);
	const successUrl = `${baseUrls.browse.channelMain}/${channelId}/subscribed`;
	const payingInvoiceSuccessUrl = `${baseUrls.browse.channelMain}/${channelId}/paying-invoice-requested`;

	const { data } = useQuery<{ channel: Channel }>(GET_PREMIUM_CHANNEL_BY_ID, {
		variables: { id: channelId },
		fetchPolicy: 'cache-first'
	});

	const { data: me, loading: billingAccountsLoading } = useQuery<{ myBillingAccounts: Account[] }>(MY_BILLING_ACCOUNTS);
	const accountsToSelectOption = (acc: Account[]): SelectOption[] => acc.map(a => ({ label: a.id, value: a.id }));

	const fetchBillingAccounts = (filter: string, callback: (options: SelectOption[]) => void) => {
		client.query<{ myBillingAccounts: Account[] }>({
			query: MY_BILLING_ACCOUNTS,
			fetchPolicy: 'cache-first',
			variables: {
				filter: filter.trim().toUpperCase()
			}
		}).then((response) => {
			const accounts = response.data.myBillingAccounts;
			const selectOptions = accountsToSelectOption(accounts);
			callback(selectOptions);
		}).catch(error => {
			printLog(
				LogLevel.ERROR,
				LogAction.GetAccounts,
				`Unable to get accounts using filter: ${filter ?? '<undefined>'}`,
				error
			);
		});
	};

	useEffect(() => {
		if (!data) return;

		if (data.channel.userChannelSubscription.subscribed &&
			data.channel.userChannelSubscription.subscriptionType !== ChannelSubscriptionType.Trial) {
			history.replace(`${baseUrls.browse.channelMain}/${channelId}/`);
		}
	}, [data, channelId, history]);

	useEffect(() => {
		if (!me) return;
		setBillingAccounts(accountsToSelectOption(me.myBillingAccounts));
	}, [me])

	useEffect(() => {
		if (paymentType || isPaymentFormShown) setPaymentTypeChosen(false);
	}, [paymentType, isPaymentFormShown])

	if (!data || billingAccountsLoading) return <Loader />;

	const channel = data.channel;
	const myBillingAccounts = me?.myBillingAccounts || [];
	const noBillingAccounts = myBillingAccounts.length < 1;
	const noBillingAccountsMessage = 'To bill to a clearing account you need to be onboarded as a Marex client. Contact the Neon sales team at neonsales@marex.com to find out more.';

	return (
		<Grid container>
			<Grid item xs={12}>
				<Grid container spacing={4}>
					<Grid item xs={12} className={classes.breadcrumbs}>
						<Breadcrumbs>
							<Link to={baseUrls.channels.main}>All channels</Link>
							<Link to={`${baseUrls.browse.channelMain}/${data.channel._id}`}>{data.channel.name}</Link>
							<Link to={getChannelSubscriptionLink(channelId)}>Subscribe</Link>
							<BreadcrumbsText>Confirm</BreadcrumbsText>
						</Breadcrumbs>
					</Grid>
					<Grid item xs={12}>
						<Heading styleName={'title1'} xs="small" md="medium" xl="large" className={classes.header} >
							Confirm subscription
						</Heading>
						<TextBlock styleName="footnote" ink="muted">
							Choose how you want to pay your monthly subscription.
						</TextBlock>
					</Grid>
					<Grid item xs={12}>
						<Grid container spacing={4} justifyContent="space-between">
							<Grid item xs={12} md={8} lg={6}>
								<Grid container spacing={4}>
									<Grid item xs={12} >
										<Grid container spacing={1}>
											<Grid item xs={12} ><TextBlock styleName="body" >Get access to Premium content</TextBlock></Grid>
											<Grid item xs={12} ><TextBlock styleName="largeBody1">{`$${channel.price}/month*`}</TextBlock></Grid>
											<Grid item xs={12} ><TextBlock styleName="body" color="grey" shade={400}>*Price excludes applicable sales taxes</TextBlock></Grid>
										</Grid>
									</Grid>
									{
										!isPaymentFormShown && (
											<>
												<Grid item xs={12}>
													<Grid container spacing={2}>
														<Grid item xs={12}>
															<Radio
																id="credit-card-radio-button"
																checked={paymentType === ChannelSubscriptionType.Stripe}
																onChange={() => setPaymentType(ChannelSubscriptionType.Stripe)}
																value={ChannelSubscriptionType.Stripe}
																label="Pay with credit or debit card"
																className={classes.radio}
															/>
														</Grid>
														<Grid item xs={12}>
															<Tooltip title={noBillingAccounts ? noBillingAccountsMessage : ''}>
																<div>
																	<Radio
																		id="marex-account-radio-button"
																		checked={paymentType === ChannelSubscriptionType.Account}
																		onChange={() => setPaymentType(ChannelSubscriptionType.Account)}
																		value={ChannelSubscriptionType.Account}
																		label="Bill to my Marex Clearing account"
																		className={[classes.radio, noBillingAccounts && classes.disabledRadio].filter(n => !!n).join(' ')}
																		disabled={noBillingAccounts}
																	/>
																</div>
															</Tooltip>
														</Grid>
														<Grid item xs={12}>
															<Radio
																id="invoice-radio-button"
																checked={paymentType === 'external'}
																onChange={() => setPaymentType(ChannelSubscriptionType.External)}
																value={ChannelSubscriptionType.External}
																label="Pay with invoice"
																className={classes.radio}
															/>
														</Grid>
													</Grid>
												</Grid>
												{
													isPaymentTypeChosen && !isPaymentFormShown && (
														<Grid item xs={12}>
															<ErrorText>Payment type should be chosen</ErrorText>
														</Grid>
													)
												}
												<Grid item xs={12}>
													<Button
														colorType="primary"
														onClick={() => {
															setPaymentTypeChosen(true);
															paymentType && setPaymentFormShown(true)
														}}
													>

														Continue
													</Button>
												</Grid>
											</>
										)
									}
									{
										isPaymentFormShown && (
											<>
												{paymentType === ChannelSubscriptionType.Account ? (
													<Grid item xs={12}>
														<PremiumChannelAccountPayment
															channelId={channelId}
															channelName={data.channel.name}
															fetchBillingAccounts={fetchBillingAccounts}
															successUrl={successUrl}
															billingAccounts={billingAccounts}
															goBack={() => setPaymentFormShown(false)}
														/>
													</Grid>
												) : <></>}
												{
													paymentType === ChannelSubscriptionType.Stripe && (
														<Grid item xs={12}>
															<StripePaymentStepper
																channelId={channelId}
																successUrl={successUrl}
																goBack={() => setPaymentFormShown(false)}
															/>
														</Grid>
													)
												}
												{
													paymentType === ChannelSubscriptionType.External && (
														<Grid item xs={12}>
															<PremiumChannelInvoicePayment
																channelId={channelId}
																successUrl={payingInvoiceSuccessUrl}
																goBack={() => setPaymentFormShown(false)}
															/>
														</Grid>
													)
												}
											</>
										)
									}
								</Grid>
							</Grid>
							<Grid item xs={12} md={4} lg={4} >
								<FurtherAssistanceEmail />
							</Grid>
						</Grid>
					</Grid>
				</Grid>
			</Grid>
		</Grid>
	);
};

export default PremiumChannelPaymentScreen;
